import ComplexTable from "./components/ComplexTable";
import { IoAdd } from "react-icons/io5";
import { useCallback, useEffect, useState, useRef } from "react";
import { usePermission } from "hooks/usePermission";
import axios from "axios";
import { API_BASE_URL } from "utils/api";

const Tables = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    country: "",
    state: "",
    district: "",
    source: "",
    priority: "",
    status: "",
    sort: "latest_updated",
    folderId: "",
  });

  // Track if component has already fetched on mount
  const hasInitialFetchedRef = useRef(false);

  // no folderId dependencies

  const { can } = usePermission();
  const canCreateLead = can("lead_management", "create");

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (filters.search) params.append("search", filters.search);
      if (filters.country) params.append("country", filters.country);
      if (filters.state) params.append("state", filters.state);
      if (filters.district) params.append("district", filters.district);
      if (filters.source) params.append("source", filters.source);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.sort) params.append("sort", filters.sort);

      const endpoint = filters.folderId 
        ? `${API_BASE_URL}/leads/folder/${filters.folderId}`
        : `${API_BASE_URL}/leads`;

      const response = await axios.get(`${endpoint}?${params.toString().replace(/%2B/g, "+")}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const leads = response?.data?.data?.data || [];
      const totalCount = response?.data?.data?.total || 0;

      const normalizedLeads = leads.map((lead) => ({
        ...lead,
        id: lead._id,
        priority: lead.priority
          ? `${lead.priority.charAt(0).toUpperCase()}${lead.priority
            .slice(1)
            .toLowerCase()}`
          : "",
        status: lead.status
          ? `${lead.status.charAt(0).toUpperCase()}${lead.status
            .slice(1)
            .toLowerCase()}`
          : "",
      }));

      setTableData(normalizedLeads);
      setTotal(totalCount);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  const handleFilterChange = useCallback((key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFiltersChange = useCallback((nextFilters) => {
    setPage(1);
    setFilters(nextFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setPage(1);
    setFilters({
      search: "",
      country: "",
      state: "",
      district: "",
      source: "",
      priority: "",
      status: "",
      sort: "latest_updated",
      folderId: "",
    });
  }, []);

  // Fetch leads only on initial mount
  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Fetch when page, limit, or filters change (using state values directly)
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchLeads();
    } else {
      hasInitialFetchedRef.current = true;
    }
  }, [page, limit, filters, fetchLeads]);

  useEffect(() => {
    if (!isAddModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAddModalOpen]);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.show]);

  return (
    <div>
      {toast.show && (
        <div
          className={`fixed right-6 top-20 z-[60] rounded-lg px-4 py-3 text-sm font-semibold shadow-md backdrop-blur-sm ${toast.type === "error"
              ? "border border-red-400/70 bg-red-500/20 text-red-700"
              : "border border-green-400/70 bg-green-500/20 text-green-700"
            }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header and Action Buttons */}
      <div className="mb-4 mt-5 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          Lead Management
        </h2>
        <div className="ml-auto flex items-center gap-4">
          {canCreateLead && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white transition-colors hover:bg-brand-600"
            >
              <IoAdd className="h-5 w-5" />
              Add Lead
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 grid h-full grid-cols-1 gap-5 md:grid-cols-1">
        <ComplexTable
          tableData={tableData}
          isLoading={loading}
          error={error}
          filters={filters}
          total={total}
          onFilterChange={handleFilterChange}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          currentPage={page}
          pageSize={limit}
          totalPages={Math.max(1, Math.ceil(total / limit))}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
          isAddModalOpen={isAddModalOpen}
          onCloseAddModal={() => setIsAddModalOpen(false)}
          onLeadCreated={fetchLeads}
          showToast={showToast}
        />
      </div>
    </div>
  );
};

export default Tables;
